import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { useActiveGstRates } from '../../hooks/useGstRates';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'draft' | 'sent' | 'awarded' | 'ordered' | 'cancelled';

interface RfqRow {
  id: string;
  rfqNumber: string;
  rfqDate: string;
  dueDate: string | null;
  status: Status;
  indentNumber: string | null;
  items: number;
  vendors: number;
  quotes: number;
  overdue: boolean;
}
interface RfqPage {
  data: RfqRow[];
  total: number;
  totalPages: number;
}
interface Quote {
  vendorId: string;
  unitPrice: number;
  taxRate: number;
  amount: number;
  lowest: boolean;
  // On the approved-vendor list of the item
  approved: boolean;
}
interface RfqItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  quantity: number;
  awardedVendorId: string | null;
  awardReason: string | null;
  purchaseOrderNumber: string | null;
  // May only be ordered from its approved vendors
  approvedVendorsOnly: boolean;
  quotes: Quote[];
  lowestUnitPrice: number | null;
}
interface RfqVendor {
  id: string;
  vendorId: string;
  vendorName: string;
  status: 'invited' | 'quoted' | 'declined';
  quoteReference: string | null;
  quoteDate: string | null;
  validUntil: string | null;
  deliveryDays: number | null;
  paymentTermsDays: number | null;
  notes: string | null;
  expired: boolean;
  itemsQuoted: number;
  quotedAmount: number;
  lowestOn: number;
}
interface Rfq {
  id: string;
  rfqNumber: string;
  rfqDate: string;
  dueDate: string | null;
  status: Status;
  notes: string | null;
  indentNumber: string | null;
  cancelReason: string | null;
  items: RfqItem[];
  vendors: RfqVendor[];
  awardedAmount: number;
  lowestAmount: number;
  purchaseOrders?: string[];
}
interface Summary {
  drafts: number;
  awaitingQuotes: number;
  toOrder: number;
  orderedThisMonth: number;
}
interface VendorChoice {
  id: string;
  name: string;
  code: string;
  isActive?: boolean;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
}
interface IndentForRfq {
  id: string;
  indentNumber: string;
  status: string;
  items: { productId: string; productName: string; sku: string; unit: string; pendingQuantity: number }[];
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const cellHead = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  sent: { label: 'Awaiting quotes', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  awarded: { label: 'Vendor chosen', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  ordered: { label: 'Ordered', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};
const VENDOR_STATUS: Record<RfqVendor['status'], { label: string; className: string }> = {
  invited: { label: 'No quote yet', className: 'bg-gray-50 text-gray-600 ring-gray-500/20' },
  quoted: { label: 'Quoted', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  declined: { label: 'Declined', className: 'bg-red-50 text-red-700 ring-red-600/20' },
};

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const rfqApi = {
  page: async (params: object): Promise<RfqPage> => (await api.get('/rfqs', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/rfqs/summary')).data,
  one: async (id: string): Promise<Rfq> => (await api.get(`/rfqs/${id}`)).data,
  create: async (data: object): Promise<Rfq> => (await api.post('/rfqs', data)).data,
  act: async (id: string, action: string, data: object = {}): Promise<Rfq> => (await api.post(`/rfqs/${id}/${action}`, data)).data,
  saveQuote: async (id: string, vendorId: string, data: object): Promise<Rfq> => (await api.put(`/rfqs/${id}/quotes/${vendorId}`, data)).data,
  vendors: async (): Promise<VendorChoice[]> => {
    const result = (await api.get('/vendors', { params: { limit: 100 } })).data;
    return ((Array.isArray(result) ? result : result?.data ?? []) as VendorChoice[]).filter((vendor) => vendor.isActive !== false);
  },
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
  indent: async (id: string): Promise<IndentForRfq> => (await api.get(`/indents/${id}`)).data,
};

// ---------------------------------------------------------------- raise an RFQ

interface FormLine {
  productId: string;
  name: string;
  sku: string;
  unit: string;
  quantity: string;
}

const RfqForm: React.FC<{ indentId: string; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ indentId, isPending, onCancel, onSubmit }) => {
  const { data: vendors = [], isLoading: vendorsLoading } = useQuery({ queryKey: ['rfqs', 'vendors'], queryFn: rfqApi.vendors });
  const { data: indent } = useQuery({ queryKey: ['indents', 'one', indentId], queryFn: () => rfqApi.indent(indentId), enabled: !!indentId });
  const [form, setForm] = useState({ dueDate: '', notes: '' });
  const [chosen, setChosen] = useState<string[]>([]);
  const [lines, setLines] = useState<FormLine[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: found = [], isFetching } = useQuery({ queryKey: ['rfqs', 'products', search], queryFn: () => rfqApi.products(search), enabled: !indentId && search.length >= 2 });
  const choices = found.filter((product) => !lines.some((line) => line.productId === product.id));
  const pending = (indent?.items ?? []).filter((item) => item.pendingQuantity > 0);

  const toggle = (vendorId: string) => {
    setChosen((prev) => (prev.includes(vendorId) ? prev.filter((id) => id !== vendorId) : [...prev, vendorId]));
    setErrors((prev) => {
      const { vendors: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (chosen.length === 0) next.vendors = 'Choose at least one vendor';
    if (!indentId) {
      if (lines.length === 0) next.lines = 'Add at least one item';
      lines.forEach((line, index) => {
        const quantity = Number(line.quantity);
        if (line.quantity.trim() === '' || !isQuantity(quantity) || quantity <= 0) next[`line-${index}`] = 'Enter a quantity above zero, up to 4 decimals';
      });
    }
    if (form.dueDate && form.dueDate < today()) next.dueDate = 'Not in the past';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      ...(indentId ? { indentId } : { items: lines.map((line) => ({ productId: line.productId, quantity: Number(line.quantity) })) }),
      dueDate: form.dueDate || null,
      notes: form.notes.trim() || null,
      vendorIds: chosen,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <section className="rounded-lg border border-gray-200">
        <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {indentId ? `Items of ${indent?.indentNumber ?? 'the indent'} still to order` : `Items · ${lines.length}`}
        </h4>
        {indentId ? (
          pending.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-500">{indent ? 'Everything on this indent has been ordered.' : 'Loading…'}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {pending.map((item) => (
                <li key={item.productId} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
                  <span className="text-gray-900">
                    {item.productName} <span className="text-xs text-gray-500">{item.sku}</span>
                  </span>
                  <span className="text-gray-900 tabular-nums">
                    {formatNumber(item.pendingQuantity)} <span className="text-gray-500">{item.unit}</span>
                  </span>
                </li>
              ))}
            </ul>
          )
        ) : (
          <>
            {lines.length > 0 && (
              <ul className="divide-y divide-gray-100">
                {lines.map((line, index) => (
                  <li key={line.productId} className="space-y-1 px-3 py-2">
                    <div className="flex items-center gap-3">
                      <p className="min-w-0 flex-1 text-sm text-gray-900">
                        {line.name} <span className="text-xs text-gray-500">{line.sku}</span>
                      </p>
                      <input type="number" min="0" step={QUANTITY_STEP} value={line.quantity} onChange={(event) => setLines((prev) => prev.map((row, position) => (position === index ? { ...row, quantity: event.target.value } : row)))} aria-label={`Quantity of ${line.name}`} className={cn(inputClass, 'w-28 py-1 text-right tabular-nums', errors[`line-${index}`] && inputErrorClass)} />
                      <span className="w-10 text-xs text-gray-500">{line.unit}</span>
                      <button type="button" onClick={() => setLines((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove ${line.name}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {errors[`line-${index}`] && <p className="text-sm text-red-600">{errors[`line-${index}`]}</p>}
                  </li>
                ))}
              </ul>
            )}
            <div className="p-3">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input id="rq-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU to add" aria-label="Search products to add" className={cn(inputClass, 'pl-9', errors.lines && inputErrorClass)} />
              </div>
              {errors.lines && <p className="mt-1 text-sm text-red-600">{errors.lines}</p>}
              {search.length >= 2 && (
                <ul className="mt-1 max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                  {choices.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
                  ) : (
                    choices.map((product) => (
                      <li key={product.id}>
                        <button type="button" onClick={() => { setLines((prev) => [...prev, { productId: product.id, name: product.name, sku: product.sku, unit: product.unit, quantity: '' }]); setSearchInput(''); setSearch(''); }} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50">
                          <span>
                            <span className="font-medium text-gray-900">{product.name}</span> <span className="text-gray-500">{product.sku}</span>
                          </span>
                          <span className="text-xs text-gray-500">{product.unit}</span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </>
        )}
      </section>

      <section className="rounded-lg border border-gray-200">
        <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Vendors to ask · {chosen.length}</h4>
        {vendorsLoading ? (
          <p className="px-3 py-3 text-sm text-gray-500">Loading…</p>
        ) : vendors.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500">There are no active vendors.</p>
        ) : (
          <ul className="grid max-h-48 grid-cols-1 gap-x-4 overflow-y-auto p-3 sm:grid-cols-2">
            {vendors.map((vendor) => (
              <li key={vendor.id}>
                <label className="flex items-center gap-2 py-1 text-sm text-gray-900">
                  <input type="checkbox" checked={chosen.includes(vendor.id)} onChange={() => toggle(vendor.id)} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  {vendor.name} <span className="text-xs text-gray-500">{vendor.code}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {errors.vendors && <p className="px-3 pb-2 text-sm text-red-600">{errors.vendors}</p>}
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Quotes wanted by" htmlFor="rq-due" error={errors.dueDate}>
          <input id="rq-due" type="date" min={today()} value={form.dueDate} onChange={(event) => setForm((prev) => ({ ...prev, dueDate: event.target.value }))} className={cn(inputClass, errors.dueDate && inputErrorClass)} />
        </Field>
        <Field label="Notes" htmlFor="rq-notes">
          <input id="rq-notes" type="text" maxLength={500} value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClass} />
        </Field>
      </div>

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending || (!!indentId && pending.length === 0)} className={primaryButtonClass}>
          {isPending ? 'Raising…' : 'Raise RFQ'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- the quote of one vendor

const QuoteForm: React.FC<{ rfq: Rfq; vendor: RfqVendor; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ rfq, vendor, isPending, onCancel, onSubmit }) => {
  const { optionsFor, defaultRate } = useActiveGstRates();
  const [form, setForm] = useState({
    declined: vendor.status === 'declined',
    quoteReference: vendor.quoteReference ?? '',
    quoteDate: vendor.quoteDate ?? '',
    validUntil: vendor.validUntil ?? '',
    deliveryDays: vendor.deliveryDays === null ? '' : String(vendor.deliveryDays),
    paymentTermsDays: vendor.paymentTermsDays === null ? '' : String(vendor.paymentTermsDays),
    notes: vendor.notes ?? '',
  });
  const [prices, setPrices] = useState<Record<string, { unitPrice: string; taxRate: string }>>(() =>
    Object.fromEntries(
      rfq.items.map((item) => {
        const quote = item.quotes.find((candidate) => candidate.vendorId === vendor.vendorId);
        return [item.id, { unitPrice: quote ? String(quote.unitPrice) : '', taxRate: quote ? String(quote.taxRate) : '' }];
      }),
    ),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const lines: object[] = [];
    if (!form.declined) {
      rfq.items.forEach((item) => {
        const entry = prices[item.id];
        if (entry.unitPrice.trim() === '') return;
        const unitPrice = Number(entry.unitPrice);
        if (!Number.isFinite(unitPrice) || unitPrice <= 0) next[item.id] = 'Enter a price above zero';
        else lines.push({ rfqItemId: item.id, unitPrice, taxRate: entry.taxRate === '' ? defaultRate : Number(entry.taxRate) });
      });
      if (lines.length === 0 && Object.keys(next).length === 0) next.lines = 'Enter at least one price, or tick that the vendor declined';
    }
    if (form.quoteDate && form.quoteDate > today()) next.quoteDate = 'Not in the future';
    const days = (value: string) => (value.trim() === '' ? null : Number(value));
    if (form.deliveryDays.trim() !== '' && (!Number.isInteger(Number(form.deliveryDays)) || Number(form.deliveryDays) < 0)) next.deliveryDays = 'Whole days';
    if (form.paymentTermsDays.trim() !== '' && (!Number.isInteger(Number(form.paymentTermsDays)) || Number(form.paymentTermsDays) < 0)) next.paymentTermsDays = 'Whole days';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      declined: form.declined,
      quoteReference: form.quoteReference.trim() || null,
      quoteDate: form.quoteDate || null,
      validUntil: form.validUntil || null,
      deliveryDays: days(form.deliveryDays),
      paymentTermsDays: days(form.paymentTermsDays),
      notes: form.notes.trim() || null,
      lines,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <p className="text-sm text-gray-700">
        Quote of <span className="font-semibold text-gray-900">{vendor.vendorName}</span> on {rfq.rfqNumber}
      </p>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input id="qf-declined" type="checkbox" checked={form.declined} onChange={(event) => setForm((prev) => ({ ...prev, declined: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
        The vendor declined to quote
      </label>
      {!form.declined && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Field label="Their quote no." htmlFor="qf-reference">
              <input id="qf-reference" type="text" maxLength={60} value={form.quoteReference} onChange={set('quoteReference')} className={inputClass} />
            </Field>
            <Field label="Quote date" htmlFor="qf-date" error={errors.quoteDate}>
              <input id="qf-date" type="date" max={today()} value={form.quoteDate} onChange={set('quoteDate')} className={cn(inputClass, errors.quoteDate && inputErrorClass)} />
            </Field>
            <Field label="Valid until" htmlFor="qf-valid">
              <input id="qf-valid" type="date" value={form.validUntil} onChange={set('validUntil')} className={inputClass} />
            </Field>
            <Field label="Delivery (days)" htmlFor="qf-delivery" error={errors.deliveryDays}>
              <input id="qf-delivery" type="number" min="0" step="1" value={form.deliveryDays} onChange={set('deliveryDays')} className={cn(inputClass, 'text-right tabular-nums', errors.deliveryDays && inputErrorClass)} />
            </Field>
            <Field label="Payment (days)" htmlFor="qf-payment" error={errors.paymentTermsDays}>
              <input id="qf-payment" type="number" min="0" step="1" value={form.paymentTermsDays} onChange={set('paymentTermsDays')} className={cn(inputClass, 'text-right tabular-nums', errors.paymentTermsDays && inputErrorClass)} />
            </Field>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={cellHead}>Item</th>
                  <th scope="col" className={cn(cellHead, 'text-right')}>Quantity</th>
                  <th scope="col" className={cn(cellHead, 'text-right')}>Price per unit</th>
                  <th scope="col" className={cn(cellHead, 'text-right')}>GST %</th>
                  <th scope="col" className={cn(cellHead, 'text-right')}>Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rfq.items.map((item) => {
                  const entry = prices[item.id];
                  const price = Number(entry.unitPrice);
                  return (
                    <tr key={item.id}>
                      <td className="px-3 py-1.5 text-sm text-gray-900">
                        {item.productName} <span className="text-xs text-gray-500">{item.sku}</span>
                        {errors[item.id] && <span className="block text-sm text-red-600">{errors[item.id]}</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-700 tabular-nums">
                        {formatNumber(item.quantity)} {item.unit}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <input type="number" min="0" step="0.01" value={entry.unitPrice} onChange={(event) => setPrices((prev) => ({ ...prev, [item.id]: { ...prev[item.id], unitPrice: event.target.value } }))} placeholder="Not quoted" aria-label={`Price of ${item.productName}`} className={cn(inputClass, 'w-32 py-1 text-right tabular-nums', errors[item.id] && inputErrorClass)} />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <select value={entry.taxRate === '' ? String(defaultRate) : entry.taxRate} onChange={(event) => setPrices((prev) => ({ ...prev, [item.id]: { ...prev[item.id], taxRate: event.target.value } }))} aria-label={`GST rate of ${item.productName}`} className={cn(inputClass, 'w-24 py-1')}>
                          {optionsFor(entry.taxRate).map((rate) => (<option key={rate} value={rate}>{rate}%</option>))}
                        </select>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-900 tabular-nums">{price > 0 ? formatMoney(price * item.quantity) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {errors.lines && <p className="text-sm text-red-600">{errors.lines}</p>}
        </>
      )}
      <Field label="Notes" htmlFor="qf-notes">
        <input id="qf-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} />
      </Field>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Back to the RFQ
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : 'Save Quote'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one RFQ: the comparison

interface PanelProps {
  rfqId: string;
  canEdit: boolean;
  canAward: boolean;
  canOrder: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const RfqPanel: React.FC<PanelProps> = ({ rfqId, canEdit, canAward, canOrder, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: rfq, isLoading, isError, error } = useQuery({ queryKey: ['rfqs', 'one', rfqId], queryFn: () => rfqApi.one(rfqId) });
  const [quoting, setQuoting] = useState<string | null>(null);
  const [awards, setAwards] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // The choices start from what is saved
  useEffect(() => {
    if (!rfq) return;
    setAwards(Object.fromEntries(rfq.items.map((item) => [item.id, item.awardedVendorId ?? ''])));
    setReasons(Object.fromEntries(rfq.items.map((item) => [item.id, item.awardReason ?? ''])));
  }, [rfq]);

  const store = (saved: Rfq) => {
    queryClient.setQueryData(['rfqs', 'one', rfqId], saved);
    onChanged();
  };
  const fail = (fallback: string) => (actionError: unknown) => toast.error(getApiErrorMessage(actionError, fallback));
  const send = useMutation({ mutationFn: () => rfqApi.act(rfqId, 'send'), onSuccess: (saved) => { toast.success(`${saved.rfqNumber} marked as sent`); store(saved); }, onError: fail('The RFQ could not be marked as sent') });
  const saveQuote = useMutation({
    mutationFn: ({ vendorId, data }: { vendorId: string; data: object }) => rfqApi.saveQuote(rfqId, vendorId, data),
    onSuccess: (saved) => { toast.success('Quote saved'); store(saved); setQuoting(null); },
    onError: fail('The quote could not be saved'),
  });
  const award = useMutation({
    mutationFn: (payload: object[]) => rfqApi.act(rfqId, 'award', { awards: payload }),
    onSuccess: (saved) => { toast.success(`Awards on ${saved.rfqNumber} saved`); store(saved); },
    onError: fail('The awards could not be saved'),
  });
  const order = useMutation({
    mutationFn: () => rfqApi.act(rfqId, 'purchase-orders'),
    onSuccess: (saved) => { toast.success(`Purchase order(s) raised: ${(saved.purchaseOrders ?? []).join(', ')}`); store(saved); queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] }); queryClient.invalidateQueries({ queryKey: ['indents'] }); },
    onError: fail('The purchase orders could not be raised'),
  });
  const cancel = useMutation({
    mutationFn: () => rfqApi.act(rfqId, 'cancel', { reason: cancelReason.trim() }),
    onSuccess: (saved) => { toast.success(`${saved.rfqNumber} cancelled`); store(saved); setCancelling(false); setCancelReason(''); },
    onError: fail('The RFQ could not be cancelled'),
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !rfq) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The RFQ could not be loaded.')}</p>;

  const quotingVendor = rfq.vendors.find((vendor) => vendor.vendorId === quoting);
  if (quotingVendor) {
    return <QuoteForm key={quotingVendor.id} rfq={rfq} vendor={quotingVendor} isPending={saveQuote.isPending} onCancel={() => setQuoting(null)} onSubmit={(data) => saveQuote.mutate({ vendorId: quotingVendor.vendorId, data })} />;
  }

  const quotesOpen = rfq.status === 'sent' || rfq.status === 'awarded';
  const mayAward = quotesOpen && canAward;
  const busy = send.isPending || award.isPending || order.isPending || cancel.isPending;
  const notLowest = (item: RfqItem) => {
    const quote = item.quotes.find((candidate) => candidate.vendorId === awards[item.id]);
    return !!quote && !quote.lowest;
  };
  const changed = rfq.items.some((item) => (awards[item.id] ?? '') !== (item.awardedVendorId ?? '') || (reasons[item.id] ?? '') !== (item.awardReason ?? ''));
  const missingReason = rfq.items.some((item) => !item.purchaseOrderNumber && notLowest(item) && (reasons[item.id] ?? '').trim().length < 3);
  const anyAwarded = rfq.items.some((item) => item.awardedVendorId && !item.purchaseOrderNumber);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, STATUS[rfq.status].className)}>{STATUS[rfq.status].label}</span>
        <span>Raised {formatDate(rfq.rfqDate)}</span>
        <span>Quotes wanted by {formatDate(rfq.dueDate)}</span>
        {rfq.indentNumber && <span>For {rfq.indentNumber}</span>}
        {rfq.notes && <span className="text-gray-500">{rfq.notes}</span>}
        {rfq.cancelReason && <span className="text-gray-500">Cancelled: {rfq.cancelReason}</span>}
      </div>
      {rfq.status === 'draft' && <p className="rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700 ring-1 ring-gray-200">Send the RFQ to the vendors yourself, then mark it as sent here. Quotes are entered after that.</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={cellHead}>Item</th>
              {rfq.vendors.map((vendor) => (
                <th key={vendor.id} scope="col" className={cn(cellHead, 'text-right normal-case tracking-normal')}>
                  <span className="block text-sm font-semibold text-gray-900">{vendor.vendorName}</span>
                  <span className={cn(badgeClass, VENDOR_STATUS[vendor.status].className)}>{VENDOR_STATUS[vendor.status].label}</span>
                  {quotesOpen && canEdit && (
                    <button type="button" onClick={() => setQuoting(vendor.vendorId)} className="ml-2 text-xs font-semibold text-indigo-600 hover:text-indigo-500" aria-label={`Enter the quote of ${vendor.vendorName}`}>
                      {vendor.status === 'invited' ? 'Enter quote' : 'Change'}
                    </button>
                  )}
                </th>
              ))}
              <th scope="col" className={cellHead}>Purchase order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rfq.items.map((item) => (
              <React.Fragment key={item.id}>
                <tr>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {item.productName} <span className="text-xs text-gray-500">{item.sku}</span>
                    <span className="block text-xs text-gray-500 tabular-nums">
                      {formatNumber(item.quantity)} {item.unit}
                    </span>
                  </td>
                  {rfq.vendors.map((vendor) => {
                    const quote = item.quotes.find((candidate) => candidate.vendorId === vendor.vendorId);
                    const chosen = awards[item.id] === vendor.vendorId;
                    return (
                      <td key={vendor.id} className={cn('whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums', chosen && 'bg-indigo-50')}>
                        {quote ? (
                          <>
                            <span className={cn('font-medium', quote.lowest ? 'text-green-700' : 'text-gray-900')}>{formatMoney(quote.unitPrice)}</span>
                            {quote.lowest && <span className={cn(badgeClass, 'ml-1 bg-green-50 text-green-700 ring-green-600/20')}>Lowest</span>}
                            <span className="block text-xs text-gray-500">{formatMoney(quote.amount)} · GST {formatNumber(quote.taxRate)}%</span>
                            {item.approvedVendorsOnly && !quote.approved ? (
                              <span className="mt-1 block text-xs font-medium text-red-600">Not an approved vendor for this item</span>
                            ) : mayAward && !item.purchaseOrderNumber ? (
                              <label className="mt-1 inline-flex items-center gap-1 text-xs text-gray-700">
                                <input type="radio" name={`award-${item.id}`} checked={chosen} onChange={() => setAwards((prev) => ({ ...prev, [item.id]: vendor.vendorId }))} aria-label={`Give ${item.productName} to ${vendor.vendorName}`} className="h-3.5 w-3.5 border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                Choose
                              </label>
                            ) : (
                              chosen && <span className="mt-1 block text-xs font-semibold text-indigo-700">Chosen</span>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                    {item.purchaseOrderNumber ?? (mayAward && awards[item.id] ? (
                      <button type="button" onClick={() => setAwards((prev) => ({ ...prev, [item.id]: '' }))} className="text-xs font-semibold text-gray-500 hover:text-gray-700" aria-label={`Take back the choice for ${item.productName}`}>
                        Take back
                      </button>
                    ) : '—')}
                  </td>
                </tr>
                {!item.purchaseOrderNumber && notLowest(item) && (
                  <tr>
                    <td colSpan={rfq.vendors.length + 2} className="bg-amber-50 px-3 py-2">
                      <label className="flex flex-wrap items-center gap-2 text-sm text-amber-900">
                        Not the lowest price for {item.productName}. Why this vendor?
                        <input type="text" maxLength={300} disabled={!mayAward} value={reasons[item.id] ?? ''} onChange={(event) => setReasons((prev) => ({ ...prev, [item.id]: event.target.value }))} aria-label={`Reason for the choice on ${item.productName}`} className={cn(inputClass, 'w-96 max-w-full py-1')} />
                      </label>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 text-sm">
            <tr>
              <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Quoted in all</td>
              {rfq.vendors.map((vendor) => (
                <td key={vendor.id} className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-900">
                  {vendor.itemsQuoted > 0 ? formatMoney(vendor.quotedAmount) : '—'}
                  {vendor.itemsQuoted > 0 && <span className="block text-xs text-gray-500">{vendor.itemsQuoted} of {rfq.items.length} items · lowest on {vendor.lowestOn}</span>}
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Delivery · payment</td>
              {rfq.vendors.map((vendor) => (
                <td key={vendor.id} className="whitespace-nowrap px-3 py-2 text-right text-gray-700">
                  {vendor.deliveryDays === null ? '—' : `${vendor.deliveryDays} days`} · {vendor.paymentTermsDays === null ? '—' : `${vendor.paymentTermsDays} days`}
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Quote · valid until</td>
              {rfq.vendors.map((vendor) => (
                <td key={vendor.id} className="whitespace-nowrap px-3 py-2 text-right text-gray-700">
                  {vendor.quoteReference ?? '—'} · <span className={vendor.expired ? 'font-medium text-red-600' : undefined}>{formatDate(vendor.validUntil)}</span>
                </td>
              ))}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-gray-500">Prices are per unit, before GST. "Lowest" marks the lowest price quoted for the item; delivery and payment terms are shown so that the choice can weigh them.</p>

      {(rfq.status === 'awarded' || rfq.status === 'ordered') && (
        <p className="text-sm text-gray-700">
          The vendors chosen come to <span className="font-semibold text-gray-900">{formatMoney(rfq.awardedAmount)}</span> before GST
          {rfq.awardedAmount > rfq.lowestAmount ? `, ${formatMoney(rfq.awardedAmount - rfq.lowestAmount)} more than the lowest quotes for the same items.` : ', the lowest quoted for these items.'}
        </p>
      )}

      {cancelling ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Field label="Why is it cancelled?" htmlFor="rp-cancel" required>
            <input id="rp-cancel" type="text" maxLength={300} value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} className={inputClass} />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setCancelling(false); setCancelReason(''); }} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" disabled={busy || cancelReason.trim().length < 3} onClick={() => cancel.mutate()} className={dangerButtonClass}>
              Confirm Cancelling
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Close Window
          </button>
          {rfq.status !== 'ordered' && rfq.status !== 'cancelled' && canEdit && (
            <button type="button" onClick={() => setCancelling(true)} disabled={busy} className={secondaryButtonClass}>
              Cancel RFQ
            </button>
          )}
          {rfq.status === 'draft' && canEdit && (
            <button type="button" onClick={() => send.mutate()} disabled={busy} className={primaryButtonClass}>
              Mark as Sent
            </button>
          )}
          {mayAward && (
            <button
              type="button"
              onClick={() => award.mutate(rfq.items.filter((item) => !item.purchaseOrderNumber).map((item) => ({ rfqItemId: item.id, vendorId: awards[item.id] || null, reason: notLowest(item) ? (reasons[item.id] ?? '').trim() : null })))}
              disabled={busy || !changed || missingReason}
              title={missingReason ? 'Say why a vendor that is not the lowest is chosen' : undefined}
              className={changed ? primaryButtonClass : secondaryButtonClass}
            >
              {award.isPending ? 'Saving…' : 'Save Choices'}
            </button>
          )}
          {rfq.status === 'awarded' && canOrder && (
            <button type="button" onClick={() => order.mutate()} disabled={busy || changed || !anyAwarded} title={changed ? 'Save the choices first' : undefined} className={primaryButtonClass}>
              {order.isPending ? 'Raising…' : 'Raise Purchase Orders'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Rfqs: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('purchase.create');
  const canEdit = can('purchase.update');
  const canAward = can('purchase.approve');
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const indentFromLink = searchParams.get('indent') ?? '';
  const openFromLink = searchParams.get('open');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [formOpen, setFormOpen] = useState(!!indentFromLink);
  const [viewingId, setViewingId] = useState<string | null>(openFromLink);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['rfqs', 'page', { page, limit, search, status: statusFilter }],
    queryFn: () => rfqApi.page({ page, limit, search, status: statusFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['rfqs', 'summary'], queryFn: rfqApi.summary });
  const { data: viewing } = useQuery({ queryKey: ['rfqs', 'one', viewingId], queryFn: () => rfqApi.one(viewingId as string), enabled: !!viewingId });

  const refreshLists = () => ['page', 'summary'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['rfqs', key] }));
  const clearLink = () => {
    if (indentFromLink || openFromLink) setSearchParams({}, { replace: true });
  };
  const closeForm = () => {
    setFormOpen(false);
    clearLink();
  };
  const create = useMutation({
    mutationFn: rfqApi.create,
    onSuccess: (rfq) => {
      toast.success(`${rfq.rfqNumber} raised`);
      queryClient.setQueryData(['rfqs', 'one', rfq.id], rfq);
      queryClient.invalidateQueries({ queryKey: ['indents'] });
      refreshLists();
      closeForm();
      setViewingId(rfq.id);
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The RFQ could not be raised')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!statusFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'Drafts', value: formatNumber(summary?.drafts ?? 0), hint: 'Not yet with the vendors' },
    { label: 'Awaiting quotes', value: formatNumber(summary?.awaitingQuotes ?? 0), hint: 'Sent; quotes being entered' },
    { label: 'To order', value: formatNumber(summary?.toOrder ?? 0), hint: 'Vendor chosen, no purchase order yet' },
    { label: 'Ordered', value: formatNumber(summary?.orderedThisMonth ?? 0), hint: 'This month' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">RFQs &amp; Quote Comparison</h1>
          <p className="mt-1 text-sm text-gray-500">The same items asked of several vendors, their quotes side by side, a vendor chosen item by item, and purchase orders raised from the choice.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Raise RFQ
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by RFQ or indent number, vendor or product" aria-label="Search RFQs" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-48')}>
            <option value="">All statuses</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>RFQ</th>
                <th scope="col" className={thClass}>Indent</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Items</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Quotes in</th>
                <th scope="col" className={thClass}>Wanted by</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The RFQs could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No RFQ matches' : 'No RFQs yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Raise an RFQ here, or from an approved indent.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.rfqNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{formatDate(row.rfqDate)}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.indentNumber ?? '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.items)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.quotes)} of {formatNumber(row.vendors)}</td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-sm', row.overdue ? 'font-medium text-red-600' : 'text-gray-700')}>{formatDate(row.dueDate)}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.rfqNumber}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      {/* One window: the form while raising, the RFQ otherwise */}
      <Modal
        isOpen={formOpen || !!viewingId}
        onClose={() => (formOpen ? closeForm() : (setViewingId(null), clearLink()))}
        title={formOpen ? 'Raise RFQ' : viewing?.rfqNumber ?? 'RFQ'}
        size={formOpen ? 'xl' : '2xl'}
      >
        {formOpen ? (
          <RfqForm indentId={indentFromLink} isPending={create.isPending} onCancel={closeForm} onSubmit={(input) => create.mutate(input)} />
        ) : (
          viewingId && <RfqPanel key={viewingId} rfqId={viewingId} canEdit={canEdit} canAward={canAward} canOrder={canCreate} onClose={() => { setViewingId(null); clearLink(); }} onChanged={refreshLists} />
        )}
      </Modal>
    </div>
  );
};

export default Rfqs;
